/**
 * Le tableau de bord de l'espace Comptabilité : « qu'est-ce que la comptable
 * doit traiter aujourd'hui ? » (cadrage du 26/09/2026, § 7).
 *
 * Réduit, au Lot UI 1, à ce qui existe réellement : la banque et sa file de
 * rapprochement, le journal des ventes et son contrôle, la reprise
 * d'ouverture, les écritures en brouillon et les exercices. Depuis le Lot
 * UI 2, les nombres d'écritures mènent aux journaux et à la liste. Depuis le
 * lot trésorerie, le compte d'attente (499000) a son bloc. Achats, frais
 * Stripe et caisse n'ont pas encore de fait source : ils n'ont pas de bloc,
 * plutôt qu'un bloc à zéro qui laisserait croire qu'il n'y a rien à faire.
 *
 * Aucun geste d'ici. Le journal des ventes et le journal de trésorerie se
 * lancent sur accord explicite, pas parce que cette page s'ouvre.
 */

import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenCheck, Eye, Hourglass, Landmark, Receipt } from 'lucide-react';
import { avecRecherche, CHEMINS } from '../../../lib/navigation/chemins';
import {
  compterLesMouvementsATraiter, lireLeDernierImport,
} from '../../../lib/tableauDeBord/general.supabase';
import { aujourdhuiABruxelles, joursEntre } from '../../../lib/tableauDeBord/general';
import {
  ANOMALIES_DES_VENTES, lireLEtatDeLaReprise, lireLEtatDesVentes, lireLEtatDuNoyau,
} from '../../../lib/tableauDeBord/comptabilite.supabase';
import { STATUTS } from '../../../lib/compta/reprise/types';
import { compterLesDoublonsBancaires } from '../../../lib/payments/jumeaux.supabase';
import { lienVersLesEcritures } from '../../../lib/compta/ecritures/types';
import { lireLEtatDeLaTresorerie } from '../../../lib/compta/tresorerie/gateway.supabase';
import { mouvementsDuStatut, raisonAffichee } from '../../../lib/compta/tresorerie/types';
import { dateCourte, euros } from '../components/money';
import { Bloc, Chiffre } from '../tableauDeBord/Bloc';
import { useLecture } from '../tableauDeBord/lecture';

const mouvements = (n: number): string => `${n.toLocaleString('fr-BE')} mouvement${n > 1 ? 's' : ''}`;

const STATUTS_EXERCICE: Record<string, string> = {
  ouvert: 'ouvert',
  en_cloture: 'en clôture',
  cloture: 'clôturé',
};

export default function ComptabiliteAccueilPage() {
  const aujourdhui = useMemo(() => aujourdhuiABruxelles(), []);
  const [banque] = useLecture(useCallback(async () => {
    const [dernier, file, doublons] = await Promise.all([
      lireLeDernierImport(), compterLesMouvementsATraiter(),
      /* Un contrôle illisible ne doit pas faire tomber tout le bloc banque. */
      compterLesDoublonsBancaires().catch(() => null),
    ]);
    return { dernier, aTraiter: file.nombre, doublons };
  }, []));
  const [ventes] = useLecture(lireLEtatDesVentes);
  const [reprise] = useLecture(lireLEtatDeLaReprise);
  const [noyau] = useLecture(lireLEtatDuNoyau);
  const [tresorerie] = useLecture(lireLEtatDeLaTresorerie);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Comptabilité</h1>
        <p className="text-gray-600">Ce qui est à traiter, au {dateCourte(aujourdhui)}.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Bloc
          titre="Banque"
          icone={<Landmark className="h-4 w-4" />}
          lecture={banque}
          lien={{ to: CHEMINS.comptabilite.rapprochement, libelle: 'Rapprocher' }}
        >
          {({ dernier, aTraiter, doublons }) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Chiffre
                  valeur={dernier ? dateCourte(dernier.creeLe) : '—'}
                  libelle={dernier
                    ? `dernier import, il y a ${joursEntre(dernier.creeLe, aujourdhui)} j${dernier.periodeFin ? ` — mouvements jusqu’au ${dateCourte(dernier.periodeFin)}` : ''}`
                    : 'aucun import'}
                />
                <Chiffre valeur={aTraiter} libelle="mouvements à imputer" ton={aTraiter > 0 ? 'attention' : 'neutre'} />
                {/* Le contrôle des doublons d'import (relevé CODA ↔ export CSV), né du 27/09/2026 : il doit être à zéro. */}
                <Chiffre
                  valeur={doublons ? doublons.doublons : '—'}
                  libelle={doublons ? 'mouvements écrits deux fois (relevé et export CSV)' : 'contrôle des doublons illisible'}
                  ton={doublons && doublons.doublons > 0 ? 'alerte' : 'neutre'}
                />
                <Chiffre
                  valeur={doublons ? doublons.probables : '—'}
                  libelle="doublons probables à vérifier (file de rapprochement)"
                  ton={doublons && doublons.probables > 0 ? 'attention' : 'neutre'}
                />
              </div>
            </div>
          )}
        </Bloc>

        {/* Le journal de trésorerie (lot T1) : le 499000 est une file de problèmes, pas un cimetière. */}
        <Bloc titre="Compte d’attente (499000)" icone={<Hourglass className="h-4 w-4" />} lecture={tresorerie}
              lien={{ to: CHEMINS.comptabilite.attente, libelle: 'Ouvrir la file' }}>
          {(t) => {
            const aComptabiliser = mouvementsDuStatut(t, 'a_comptabiliser');
            const retenus = mouvementsDuStatut(t, 'retenu_doublon');
            const extournes = mouvementsDuStatut(t, 'extourne_hors_geste');
            const principales = t.parRaison.slice(0, 3);
            const jusquAu = Object.entries(t.encodeJusquAu).filter(([, d]) => d);
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Chiffre valeur={euros(t.attente.entree.montantCents)}
                           libelle={`entrées en attente (${mouvements(t.attente.entree.nombre)})`}
                           ton={t.attente.entree.nombre > 0 ? 'attention' : 'neutre'} />
                  <Chiffre valeur={euros(t.attente.sortie.montantCents)}
                           libelle={`sorties en attente (${mouvements(t.attente.sortie.nombre)})`}
                           ton={t.attente.sortie.nombre > 0 ? 'attention' : 'neutre'} />
                  <Chiffre valeur={aComptabiliser.nombre} libelle="mouvements à comptabiliser"
                           ton={aComptabiliser.nombre > 0 ? 'attention' : 'neutre'} />
                  <Chiffre valeur={t.anomalies} libelle={`anomalies au contrôle de la trésorerie (${t.signaux} signaux)`}
                           ton={t.anomalies > 0 ? 'alerte' : 'neutre'} />
                </div>
                {principales.length > 0 && (
                  <ul className="text-sm text-gray-700">
                    {principales.map((r) => (
                      <li key={`${r.sens}:${r.raison}`}>
                        {euros(r.montantCents)} — {raisonAffichee(r.raison)} ({r.sens === 'entree' ? 'entrées' : 'sorties'})
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-gray-500">
                  {jusquAu.length === 0
                    ? 'Aucun mouvement encore comptabilisé : le journal de trésorerie se lance sur accord.'
                    : `Banque comptabilisée jusqu’au ${jusquAu.map(([j, d]) => `${dateCourte(d as string)} (${j})`).join(', ')}.`}
                  {retenus.nombre > 0 && ` ${mouvements(retenus.nombre)} ${retenus.nombre > 1 ? 'retenus' : 'retenu'} : doublon à trancher.`}
                  {extournes.nombre > 0 && ` ${mouvements(extournes.nombre)} ${extournes.nombre > 1 ? 'extournés' : 'extourné'} à la main : à trancher.`}
                </p>
              </div>
            );
          }}
        </Bloc>

        <Bloc titre="Ventes" icone={<Receipt className="h-4 w-4" />} lecture={ventes}
              lien={{ to: CHEMINS.comptabilite.factures, libelle: 'Factures et notes de crédit' }}>
          {(v) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Link to={lienVersLesEcritures({ journal: 'VEN' })} className="hover:opacity-80"
                      title="Les écritures du journal des ventes">
                  <Chiffre valeur={v.ecrituresValidees.toLocaleString('fr-BE')} libelle="écritures du journal des ventes" />
                </Link>
                <Chiffre
                  valeur={v.anomalies.reduce((t, a) => t + a.nombre, 0)}
                  libelle="pièces en écart au contrôle des ventes"
                  ton={v.anomalies.some((a) => a.anomalie !== 'a_comptabiliser') ? 'alerte'
                    : v.anomalies.length > 0 ? 'attention' : 'neutre'}
                />
              </div>
              {v.anomalies.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Le journal des ventes et le grand livre des familles disent la même chose, pièce par pièce.
                </p>
              ) : (
                <>
                  <ul className="text-sm text-gray-700">
                    {v.anomalies.map((a) => (
                      <li key={a.anomalie}>
                        {a.nombre} {ANOMALIES_DES_VENTES[a.anomalie] ?? a.anomalie}
                      </li>
                    ))}
                  </ul>
                  <ul className="divide-y divide-gray-100 text-xs">
                    {v.exemples.map((p, i) => (
                      <li key={`${p.numero ?? i}`} className="flex items-center justify-between gap-2 py-1">
                        {p.numero ? (
                          <Link to={avecRecherche(CHEMINS.comptabilite.factures, p.numero)}
                                className="font-medium text-primary-700 hover:underline">
                            {p.numero}
                          </Link>
                        ) : <span className="text-gray-500">pièce sans numéro</span>}
                        <span className="text-gray-500">
                          {dateCourte(p.datePiece)} · {ANOMALIES_DES_VENTES[p.anomalie] ?? p.anomalie}
                          {p.attenduCents !== null && ` · ${euros(p.attenduCents)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {v.anomalies.some((a) => a.anomalie === 'a_comptabiliser') && (
                    <p className="text-xs text-gray-500">
                      Le journal des ventes est idempotent ; il se relance sur accord, pas depuis cette page.
                    </p>
                  )}
                  {v.tronque && <p className="text-xs text-amber-700">Au-delà de 1 000 lignes, le détail est partiel.</p>}
                </>
              )}
            </div>
          )}
        </Bloc>

        <Bloc titre="Reprise d’ouverture" icone={<BookOpenCheck className="h-4 w-4" />} lecture={reprise}
              lien={{ to: CHEMINS.comptabilite.reprise, libelle: 'Ouvrir la reprise' }}>
          {(r) => (
            <div className="space-y-2 text-sm">
              {r.statut === null ? (
                <p className="text-gray-600">Aucune reprise ouverte{r.annee ? ` pour ${r.annee}` : ''}.</p>
              ) : (
                <p>
                  Reprise {r.annee ?? ''} :{' '}
                  <span className={`rounded-full px-2 py-0.5 text-xs ${STATUTS[r.statut].classe}`}>
                    {STATUTS[r.statut].label}
                  </span>
                </p>
              )}
              {r.bloquants.length > 0 && (
                <>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Ce qui empêche la validation</p>
                  <ul className="list-disc space-y-0.5 pl-5 text-gray-700">
                    {r.bloquants.map((b) => <li key={b.etiquette}>{b.message}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}
        </Bloc>

        <Bloc titre="À surveiller" icone={<Eye className="h-4 w-4" />} lecture={noyau}
              lien={{ to: CHEMINS.comptabilite.journaux, libelle: 'Les journaux' }}>
          {(n) => (
            <div className="space-y-3 text-sm">
              {n.brouillons > 0 ? (
                <Link to={lienVersLesEcritures({ statut: 'brouillon' })} className="block hover:opacity-80"
                      title="Les écritures en brouillon">
                  <Chiffre valeur={n.brouillons} libelle="écritures en brouillon" ton="attention" />
                </Link>
              ) : (
                <Chiffre valeur={n.brouillons} libelle="écritures en brouillon" />
              )}
              <p className="text-gray-700">
                Exercices :{' '}
                {n.exercices.length === 0
                  ? 'aucun'
                  : n.exercices.map((e) => `${e.annee} ${STATUTS_EXERCICE[e.statut] ?? e.statut}`).join(' · ')}
              </p>
            </div>
          )}
        </Bloc>
      </div>
    </div>
  );
}
